import React, { useEffect, useMemo } from "react";
import { getErrorDetails } from "../../../app/appUtils";
import { MySelect } from "../../../components/ui/Select";
import { cn } from "../../../lib/utils";
import { Company } from "../@types";
import { useGetCompaniesMutation } from "../companyApiSlice";

interface CompanyOption {
  value: string;
  label: string;
}

interface CompanyDropdownMenuProps {
  value?: string;
  onChange?: (value: CompanyOption | null) => void;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  id?: string;
  showLabel?: boolean;
  label?: string;
  error?: string;
}

const CompanyDropdownMenu: React.FC<CompanyDropdownMenuProps> = ({
  value,
  onChange,
  className,
  disabled,
  placeholder = "Company",
  name = "companies",
  id = "companies",
  showLabel = true,
  error,
  label,
}) => {
  const [
    getCompanies,
    { data: companiesData, isLoading, isError, error: errorData },
  ] = useGetCompaniesMutation();

  const companies = useMemo(() => {
    return companiesData?.data.data || [];
  }, [companiesData?.data.data]);

  useEffect(() => {
    getCompanies({
      pageNumber: 0,
      pageSize: 100,
    });
  }, [getCompanies]);

  const companiesOptions: CompanyOption[] = useMemo(() => {
    if (isLoading) {
      return [{ value: "", label: "Loading..." }];
    }

    if (isError) {
      return [{ value: "", label: "Error loading companies" }];
    }

    return companies.map((company: Company) => ({
      value: company.id,
      label: `${company.fullName}`,
    }));
  }, [companies, isLoading, isError]);

  const handleValueChange = (option: CompanyOption) => {
    onChange?.(option);
  };

  // Helper functions for MySelect
  const getValue = (option: CompanyOption) => option.value;
  const getName = (option: CompanyOption) => option.label;

  return (
    <div className={cn("w-full")}>
      <MySelect<CompanyOption>
        name={name}
        id={id}
        label={showLabel ? label || "Company" : ""}
        options={companiesOptions}
        getValue={getValue}
        getName={getName}
        value={value}
        onChange={(id) => {
          const selectedOption = companiesOptions.find(
            (option) => option.value === id
          );
          onChange?.(selectedOption || null);
        }}
        onValChange={handleValueChange}
        placeholder={placeholder}
        disabled={disabled || isLoading || isError}
        className={cn(
          "focus:ring-primary focus:border-primary",
          {
            "opacity-50": isLoading,
          },
          className
        )}
        showSearch={true}
      />

      {isError && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {getErrorDetails(errorData)?.message || "Error loading companies"}
        </p>
      )}

      {error && (
        <p className="mt-1 text-sm text-red-500" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default CompanyDropdownMenu;
